import React, {
  createContext, useState, useEffect,
  useMemo,
} from 'react';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { GET_TESTERS } from '../graphql';

// Create the context
const TestSessionContext = createContext({
  testSessionStatuses: {},
  setTestSessionStatuses: () => { },
  loadingCompletedTesters: false,
  errorCompletedTesters: null,
  refetchCompletedTesters: () => { },
  completedTesters: [],
});

// Create a provider component
export const TestSessionProvider = ({ children, projectId }) => {
  const [testSessionStatuses, setTestSessionStatuses] = useState({});
  const {
    data: completedTestersData,
    loading: loadingCompletedTesters,
    error: errorCompletedTesters,
    refetch: refetchCompletedTesters,
  } = useQuery(GET_TESTERS, {
    variables: {
      isCompleted: true,
      projectId,
    },
    fetchPolicy: 'network-only',
  });

  const { testers: completedTesters } = completedTestersData ?? { testers: [] };

  useEffect(() => {
    const testSessionsIds = completedTesters.map((tester, index) => {
      const testSession = tester.testSessions[index % tester.testSessions.length];
      return testSession;
    });

    setTestSessionStatuses((currentStatuses) => {
      const updatedStatuses = { ...currentStatuses };

      _.forEach(testSessionsIds, (testSession) => {
        if (updatedStatuses[testSession.id] === 'enqueued') {
          if (testSession.status !== 'completed') {
            updatedStatuses[testSession.id] = testSession.status;
          }
        }

        updatedStatuses[testSession.id] = testSession.status;
      });

      return updatedStatuses;
    });
  }, [completedTesters]);

  const value = useMemo(() => ({
    testSessionStatuses,
    setTestSessionStatuses,
    loadingCompletedTesters,
    errorCompletedTesters,
    refetchCompletedTesters,
    completedTesters,
  }), [completedTesters, errorCompletedTesters, loadingCompletedTesters, refetchCompletedTesters, testSessionStatuses]);

  return (
    <TestSessionContext.Provider value={value}>
      {children}
    </TestSessionContext.Provider>
  );
};

TestSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  projectId: PropTypes.string.isRequired,
};

export default TestSessionContext;
